import { useState, SetStateAction, Dispatch, useEffect } from "react";

const useSessionStorage = <T extends string>(
  key: string,
  initialValue: T | null
): [T | null, Dispatch<SetStateAction<T | null>>, () => void] => {
  const [state, setState] = useState<T | null>(() => {
    const sessionStorageValue = (sessionStorage.getItem(key) as T) || null;
    try {
      if (typeof sessionStorageValue !== "string" && sessionStorageValue) {
        sessionStorage.setItem(key, JSON.stringify(initialValue));
        return initialValue;
      } else {
        return sessionStorageValue;
      }
    } catch {
      // If user is in private mode or has storage restriction
      // sessionStorage can throw. JSON.parse and JSON.stringify
      // can throw, too.
      return initialValue;
    }
  });

  useEffect(() => {
    const handleStorageChange = () => {
      setState(() => (sessionStorage.getItem(key) as T) || null);
    };

    window.addEventListener("sessionStorageModified", handleStorageChange);
    return () => {
      window.removeEventListener("sessionStorageModified", handleStorageChange);
    };
  }, [key]);

  const removeItem = () => sessionStorage.removeItem(key);

  return [state, setState, removeItem];
};

export default useSessionStorage;
